import { ArrowLeft, Check, Eraser, Save } from "lucide-react";
import { useEffect, useState } from "react";

import { PLUTO_WRITING_INSTRUCTIONS_MAX_LENGTH } from "@bloodline/api-definition";

import usePlutoWritingPreferences from "../../hooks/usePlutoWritingPreferences";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { Textarea } from "../ui/textarea";

const EXAMPLE_INSTRUCTIONS =
  "Write in Swedish with short paragraphs. Be direct but warm. Open with the timing signal, avoid hype, and end with one low-friction question. Do not use exclamation marks.";

const formatUpdatedAt = (updatedAt: string | null): string => {
  if (!updatedAt) return "Using Pluto’s default writing style";
  const date = new Date(updatedAt);
  return Number.isNaN(date.getTime()) ? "Saved writing style" : `Last saved ${date.toLocaleString()}`;
};

type PlutoWritingPreferencesPanelProps = {
  onBack: () => void;
};

export const PlutoWritingPreferencesPanel = ({ onBack }: PlutoWritingPreferencesPanelProps) => {
  const { clear, query, save } = usePlutoWritingPreferences();
  const [instructions, setInstructions] = useState("");
  const [isDirty, setIsDirty] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const preferences = query.data;

  useEffect(() => {
    if (!preferences || isDirty) return;
    setInstructions(preferences.instructions);
  }, [isDirty, preferences]);

  const normalizedInstructions = instructions.trim();
  const isMutating = save.isPending || clear.isPending;
  const canSave =
    normalizedInstructions.length > 0 &&
    normalizedInstructions.length <= PLUTO_WRITING_INSTRUCTIONS_MAX_LENGTH &&
    normalizedInstructions !== preferences?.instructions &&
    !isMutating;

  const saveInstructions = async () => {
    if (!canSave) return;
    try {
      const saved = await save.mutateAsync(normalizedInstructions);
      setInstructions(saved.instructions);
      setIsDirty(false);
      setConfirmClear(false);
      clear.reset();
    } catch {
      // Mutation state renders a recoverable error in the panel.
    }
  };

  const clearInstructions = async () => {
    try {
      const cleared = await clear.mutateAsync();
      setInstructions(cleared.instructions);
      setIsDirty(false);
      setConfirmClear(false);
      save.reset();
    } catch {
      // Mutation state renders a recoverable error in the panel.
    }
  };

  const error = query.error ?? save.error ?? clear.error;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="border-b border-stone-200 bg-white px-5 py-4">
        <Button className="-ml-2 mb-3 h-8 gap-1.5 px-2 text-xs" onClick={onBack} variant="ghost">
          <ArrowLeft aria-hidden="true" className="h-3.5 w-3.5" />
          Back to conversation
        </Button>
        <h2 className="text-xl font-semibold text-stone-900">Writing instructions</h2>
        <p className="mt-2 text-sm leading-6 text-stone-600">
          Tell Pluto how your sales emails and SMS drafts should sound. These instructions stay private to your
          Bloodline user.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-5">
        {query.isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-52 w-full" />
          </div>
        ) : (
          <>
            <label className="text-sm font-medium text-stone-800" htmlFor="pluto-writing-instructions">
              How should Pluto phrase and structure your outreach?
            </label>
            <Textarea
              className="mt-2 min-h-[220px] resize-y bg-white text-sm leading-6"
              id="pluto-writing-instructions"
              maxLength={PLUTO_WRITING_INSTRUCTIONS_MAX_LENGTH}
              placeholder={EXAMPLE_INSTRUCTIONS}
              value={instructions}
              onChange={(event) => {
                setInstructions(event.target.value);
                setIsDirty(true);
                setConfirmClear(false);
              }}
            />
            <div className="mt-2 flex items-center justify-between gap-3 text-xs text-stone-500">
              <span>{formatUpdatedAt(preferences?.updatedAt ?? null)}</span>
              <span className="tabular-nums">
                {instructions.length}/{PLUTO_WRITING_INSTRUCTIONS_MAX_LENGTH}
              </span>
            </div>

            <button
              className="mt-5 w-full rounded-xl border border-dashed border-stone-300 bg-stone-50 p-4 text-left transition-colors hover:border-stone-500 hover:bg-stone-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-700"
              type="button"
              onClick={() => {
                setInstructions(EXAMPLE_INSTRUCTIONS);
                setIsDirty(true);
              }}
            >
              <span className="block text-xs font-medium text-stone-800">Use an example as a starting point</span>
              <span className="mt-1 block text-xs leading-5 text-stone-600">{EXAMPLE_INSTRUCTIONS}</span>
            </button>

            {error ? (
              <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-xs leading-5 text-red-800">
                Writing instructions could not be {save.error ? "saved" : clear.error ? "cleared" : "loaded"}. Try
                again.
              </div>
            ) : null}

            <div className="mt-5 rounded-lg bg-stone-100 p-3 text-xs leading-5 text-stone-600">
              Pluto’s evidence, safety, and no-invention rules always take precedence. Drafts are never sent
              automatically.
            </div>
          </>
        )}
      </div>

      <div className="border-t border-stone-200 bg-white px-5 py-4">
        {confirmClear ? (
          <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <p className="text-xs leading-5 text-amber-900">Clear your saved style and return to Pluto’s defaults?</p>
            <div className="mt-2 flex gap-2">
              <Button
                className="h-8 text-xs"
                disabled={isMutating}
                onClick={() => void clearInstructions()}
                variant="outline"
              >
                {clear.isPending ? "Clearing…" : "Yes, clear"}
              </Button>
              <Button className="h-8 text-xs" onClick={() => setConfirmClear(false)} variant="ghost">
                Cancel
              </Button>
            </div>
          </div>
        ) : null}
        <div className="flex items-center justify-between gap-3">
          <Button
            className="gap-2"
            disabled={!preferences?.instructions || isMutating}
            onClick={() => setConfirmClear(true)}
            variant="ghost"
          >
            <Eraser aria-hidden="true" className="h-4 w-4" />
            Clear
          </Button>
          <Button className="gap-2" disabled={!canSave} onClick={() => void saveInstructions()}>
            {save.isSuccess && !isDirty ? (
              <Check aria-hidden="true" className="h-4 w-4" />
            ) : (
              <Save aria-hidden="true" className="h-4 w-4" />
            )}
            {save.isPending ? "Saving…" : save.isSuccess && !isDirty ? "Saved" : "Save instructions"}
          </Button>
        </div>
      </div>
    </div>
  );
};
